import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LayoutDashboard, Gauge, Sparkles, Activity, ListFilter, Table2, MessagesSquare, Globe, Users, Boxes, PackageSearch, RefreshCw, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Sun, Moon, Trophy, MessageSquareWarning, Wrench, Star, DoorOpen, NotebookPen } from 'lucide-react';
import { displayUserName } from '../api';
import { getInitialTheme, toggleTheme, type Theme } from '../theme';
import type { AuthUser } from '../types';

export type RouteId = 'overview' | 'admin' | 'opd' | 'opdraw' | 'insights' | 'products' | 'status' | 'funnel' | 'detail' | 'chat' | 'online' | 'staff' | 'quality' | 'complaints' | 'warranty' | 'reviews' | 'tcdt';
type RouteGroup = 'overview' | 'consulting' | 'followup' | 'quality' | 'data';

const routeIcons = {
  overview: LayoutDashboard,
  admin: Gauge,
  opd: PackageSearch,
  opdraw: NotebookPen,
  insights: Sparkles,
  products: Boxes,
  status: Activity,
  funnel: ListFilter,
  detail: Table2,
  chat: MessagesSquare,
  online: Globe,
  staff: Users,
  quality: Trophy,
  complaints: MessageSquareWarning,
  warranty: Wrench,
  reviews: Star,
  tcdt: DoorOpen,
};

export const routes: Array<{ id: RouteId; hash: string; label: string; sub: string; group: RouteGroup }> = [
  { id: 'overview', hash: '#/overview', label: 'Tổng quan', sub: 'Toàn cảnh hoạt động', group: 'overview' },
  { id: 'admin', hash: '#/admin', label: 'Điều hành bán hàng', sub: 'Tổng hợp cửa hàng · cảnh báo', group: 'overview' },
  { id: 'insights', hash: '#/insights', label: 'Tư vấn tại cửa hàng', sub: 'Xu hướng · nhu cầu · lý do chưa chốt', group: 'consulting' },
  { id: 'online', hash: '#/online', label: 'Tư vấn online', sub: 'Hội thoại · nhu cầu · lý do chưa chốt', group: 'consulting' },
  { id: 'products', hash: '#/products', label: 'Nhu cầu sản phẩm', sub: 'Sản phẩm · ngành hàng · thiếu hàng', group: 'consulting' },
  { id: 'opd', hash: '#/opd', label: 'Hỏi hàng & phát sinh', sub: 'Trao đổi · sản phẩm · xử lý', group: 'consulting' },
  { id: 'opdraw', hash: '#/opdraw', label: 'Nhật ký vận hành', sub: 'Thảo luận Lark · nguyên văn theo chủ đề', group: 'consulting' },
  { id: 'funnel', hash: '#/funnel', label: 'Phễu chuyển đổi', sub: 'Tiếp cận · tư vấn · mua hàng', group: 'followup' },
  { id: 'detail', hash: '#/detail', label: 'Danh sách tư vấn', sub: 'Khách hàng · nhu cầu · trạng thái', group: 'followup' },
  { id: 'chat', hash: '#/chat', label: 'Đối soát chăm sóc', sub: 'Hội thoại · khách hàng · đơn hàng', group: 'followup' },
  { id: 'staff', hash: '#/staff', label: 'Hiệu quả nhân viên', sub: 'Khách hàng · liên hệ · kết quả', group: 'followup' },
  { id: 'quality', hash: '#/quality', label: 'KPI Chất lượng vận hành', sub: 'Scorecard · thưởng phạt · xếp hạng', group: 'quality' },
  { id: 'complaints', hash: '#/complaints', label: 'KPI Khiếu nại', sub: 'Google · Zalo · Chat · SLA', group: 'quality' },
  { id: 'warranty', hash: '#/warranty', label: 'Bảo hành', sub: 'Ngày xử lý · phiếu chờ · gỡ KPI', group: 'quality' },
  { id: 'reviews', hash: '#/reviews', label: 'Review & CSAT', sub: 'Tốt xấu · chuyển đổi review', group: 'quality' },
  { id: 'tcdt', hash: '#/tcdt', label: 'Tiêu chuẩn đón tiếp', sub: 'Vi phạm · tiêu chí · giải trình', group: 'quality' },
  { id: 'status', hash: '#/status', label: 'Chất lượng dữ liệu', sub: 'Độ đầy đủ · lịch cập nhật', group: 'data' },
];

const routeGroups: Array<{ id: RouteGroup; label: string }> = [
  { id: 'overview', label: 'Theo dõi chung' },
  { id: 'consulting', label: 'Tư vấn bán hàng' },
  { id: 'followup', label: 'Theo dõi kết quả' },
  { id: 'quality', label: 'Chất lượng vận hành' },
  { id: 'data', label: 'Dữ liệu' },
];

export function routeFromHash(): RouteId {
  const raw = (window.location.hash || '#/overview').replace(/^#\/?/, '') || 'overview';
  return routes.some(route => route.id === raw) ? raw as RouteId : 'overview';
}

interface AppLayoutProps {
  screen: RouteId;
  authUser: AuthUser;
  apiSource: string;
  refreshedAt?: string;
  isBusy: boolean;
  apiError: string;
  onRoute: (route: RouteId) => void;
  onReload: () => void;
  onLogout: () => void;
  children: ReactNode;
}

export function AppLayout({ screen, authUser, refreshedAt, isBusy, apiError, onRoute, onReload, onLogout, children }: AppLayoutProps) {
  const active = routes.find(route => route.id === screen) || routes[0];
  const ActiveIcon = routeIcons[active.id];
  // Keep labels visible by default on desktop; preserve the compact rail on small screens.
  const [navOpen, setNavOpen] = useState(() => !window.matchMedia('(max-width:720px)').matches);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const sidebarRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const flipTheme = () => setTheme(current => toggleTheme(current));

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setNavOpen(false); };
    const onPointerDown = (event: PointerEvent) => {
      if (!navOpen) return;
      if (!window.matchMedia('(max-width:720px)').matches) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (sidebarRef.current?.contains(target) || toggleRef.current?.contains(target)) return;
      setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [navOpen]);

  useEffect(() => {
    const media = window.matchMedia('(max-width:720px)');
    const syncNavigation = (event: MediaQueryListEvent) => setNavOpen(!event.matches);
    media.addEventListener('change', syncNavigation);
    return () => media.removeEventListener('change', syncNavigation);
  }, []);

  const pickRoute = (route: RouteId) => {
    onRoute(route);
    if (window.matchMedia('(max-width:720px)').matches) setNavOpen(false);
  };

  return (
    <div className={`app-shell ${navOpen ? 'nav-open' : ''}`}>
      <aside id="app-sidebar" ref={sidebarRef} className={`sidebar ${navOpen ? 'open' : ''}`}>
        <div className="side-brand">
          <div className="logo"><img className="logo-img" src="/favicon.png" alt="" /></div>
          <div className="side-brand-copy">
            <div className="side-title">Hiệu quả tư vấn</div>
            <div className="side-sub">Cửa hàng &amp; Online</div>
          </div>
          <button
            className="nav-toggle side-nav-toggle"
            aria-label={navOpen ? 'Thu gọn menu điều hướng' : 'Mở rộng menu điều hướng'}
            aria-controls="app-sidebar"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(value => !value)}
            type="button"
          >
            {navOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
          </button>
        </div>
        <nav className="side-nav" aria-label="Các báo cáo">
          {routeGroups.map(group => (
            <div className="side-nav-group" key={group.id}>
              <div className="side-nav-label">{group.label}</div>
              {routes.filter(route => route.group === group.id).map(route => {
                const Icon = routeIcons[route.id];
                return (
                  <a key={route.id} className={`side-link ${screen === route.id ? 'active' : ''}`} href={route.hash} title={route.label} aria-label={route.label} onClick={() => pickRoute(route.id)}>
                    <Icon size={16} />
                    <span>{route.label}<small>{route.sub}</small></span>
                  </a>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="side-meta">
          <b>Dữ liệu tự động cập nhật</b><br />
          Kiểm tra thời gian cập nhật ở đầu trang. Nếu số liệu bất thường, mở mục Chất lượng dữ liệu.
        </div>
      </aside>
      <main className="main">
        <div className="wrap">
          <header className="topbar">
            <div className="brand">
              <button ref={toggleRef} className="nav-toggle mobile-nav-toggle" aria-label="Hiện menu điều hướng" aria-controls="app-sidebar" aria-expanded={navOpen} onClick={() => setNavOpen(true)} type="button"><Menu size={20} /></button>
              <ActiveIcon size={22} />
              <div>
                <h1>{active.label}</h1>
                <div className="sub">
                  Dữ liệu cập nhật {refreshedAt ? new Date(refreshedAt).toLocaleString('vi-VN') : 'chưa xác định'}
                </div>
              </div>
            </div>
            <div className="actions">
              <span className="pill live">Xin chào, {displayUserName(authUser)}</span>
              <button aria-label={theme === 'dark' ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'} title={theme === 'dark' ? 'Giao diện sáng' : 'Giao diện tối'} className="btn icon" onClick={flipTheme} type="button">{theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}</button>
              <button aria-label="Tải lại dữ liệu dashboard" className="btn live" disabled={isBusy} onClick={onReload} type="button"><RefreshCw size={15} />{isBusy ? 'Đang đọc...' : 'Tải lại'}</button>
              <button aria-label="Đăng xuất" className="btn" onClick={onLogout} type="button"><LogOut size={15} />Đăng xuất</button>
              {apiError && <div className="api-note error">Không tải được dữ liệu: {apiError}</div>}
            </div>
          </header>
          <div className="route-title">
            <ActiveIcon size={16} />
            <div>
              <h2>{active.label}</h2>
              <span>{active.sub}</span>
            </div>
          </div>
          {children}
          <footer className="footer">
            Báo cáo hiệu quả tư vấn bán hàng · dữ liệu chỉ hiển thị cho người dùng đã đăng nhập.
          </footer>
        </div>
      </main>
    </div>
  );
}
